import React, {useRef, useEffect} from 'react';
import {Box, Center, Flex, HStack, Spinner, Switch, VStack} from '@chakra-ui/react';
import {TextG12, TextG14} from '../../../components/typography';
import NeighborsInterestedDisplay from '../../../components/UsersList/NeighborsInterestedDisplay';
import {PublicProfile} from '../../../hooks/usePublicProfile';
import {UserEvent} from '../../../hooks/useUserEvents';
import {useNavigate} from 'react-router-dom';
import dayjs from 'dayjs';
import MessageEventBtn from './MessageEventBtn';
import {BookmarkElement} from '../../../components/Articles';

interface PlansQuery {
  plans: UserEvent[];
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
}

interface TheirPlansProps {
  profile: PublicProfile;
  plansQuery: PlansQuery;
  sharedPlansQuery: PlansQuery;
  sharedOnly: boolean;
  setSharedOnly: (value: boolean) => void;
  onOpenArticle: (plan: UserEvent) => void;
}

const TheirPlans = ({profile, plansQuery, sharedPlansQuery, sharedOnly, setSharedOnly, onOpenArticle}: TheirPlansProps) => {
  const navigate = useNavigate();
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const activeQuery = sharedOnly ? sharedPlansQuery : plansQuery;
  const displayPlans = activeQuery.plans;

  const isSharedEvent = (plan: UserEvent) => plan.type === 'event' && plan.is_bookmarked;

  const formatDate = (dateStr: string, price: number) => {
    const d = dayjs(dateStr);
    const dateFormatted = d.format('ddd, MMM D [at] h:mm A');
    const priceStr = price === 0 ? 'Free' : `$${price}`;
    return `${dateFormatted} • ${priceStr}`;
  };

  // Build a minimal article-like object for BookmarkElement
  const toArticle = (plan: UserEvent) => ({
    ...plan,
  });

  // IntersectionObserver to load next page when sentinel is visible
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && activeQuery.hasNextPage && !activeQuery.isFetchingNextPage) {
          activeQuery.fetchNextPage();
        }
      },
      {threshold: 0.1},
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [activeQuery.hasNextPage, activeQuery.isFetchingNextPage, activeQuery.fetchNextPage]);

  return (
    <Box pb={20}>
      {/* Shared plans toggle */}
      <Flex px={4} py={3} justify="space-between" align="center" borderBottom="1px solid #F0F0F0">
        <VStack align="flex-start" spacing={0}>
          <TextG14 mt="0" fontWeight="700">
            Shared plans only
          </TextG14>
          <TextG12 mt="0" color="#757D84">
            We'll show you only things that you both saved
          </TextG12>
        </VStack>
        <Switch
          colorScheme="green"
          isChecked={sharedOnly}
          onChange={(e) => setSharedOnly(e.target.checked)}
        />
      </Flex>

      {/* Plans list */}
      <VStack spacing={4} px={4} pt={4} align="stretch">
        {activeQuery.isLoading ? (
          <Center py={12}>
            <Spinner size="lg" color="#E02F84" />
          </Center>
        ) : displayPlans.length > 0 ? (
          <>
            {displayPlans.map((plan) => (
              <Box
                key={`${plan.type}:${plan.id}`}
                border="1px solid #E5E5E5"
                borderRadius="16px"
                overflow="hidden"
                boxShadow="0 0 16px 0 rgba(0, 0, 0, 0.08)"
                cursor="pointer"
                onClick={() => onOpenArticle(plan)}>
                {/* Image */}
                <Box w="100%" h="160px" bg="#E5E5E5" position="relative">
                  {plan.image_url && (
                    <img
                      src={plan.image_url}
                      alt={plan.title}
                      style={{width: '100%', height: '100%', objectFit: 'cover'}}
                    />
                  )}
                  {plan.is_walkabout_official && (
                    <Box
                      position="absolute"
                      bottom="8px"
                      left="8px"
                      bg="#E02F84"
                      color="white"
                      px={2}
                      py={0.5}
                      borderRadius="6px"
                      fontSize="11px"
                      fontWeight="600"
                      fontFamily="Gordita">
                      Walkabout Official
                    </Box>
                  )}
                  <BookmarkElement article={toArticle(plan) as any} />
                </Box>

                {/* Details */}
                <Box p={4}>
                  <TextG14 mt="0" fontWeight="700" mb={1}>
                    {plan.title}
                  </TextG14>
                  {plan.start_date_time && (
                    <TextG12 mt="0" color="#636363" mb={1}>
                      {formatDate(plan.start_date_time, plan.price)}
                    </TextG12>
                  )}
                  {plan.place?.name && (
                    <HStack spacing={1} mb={2}>
                      <i className="fi fi-rr-marker" aria-hidden="true" style={{fontSize: '10px', color: '#636363'}} />
                      <TextG12 mt="0" color="#636363">
                        {plan.place.name}
                      </TextG12>
                    </HStack>
                  )}
                  {plan.type === 'event' && (
                    <Box mb={isSharedEvent(plan) ? 3 : 0}>
                      <NeighborsInterestedDisplay
                        articleId={plan.id}
                        articleType={plan.is_meetup ? 'meetup' : 'event'}
                        bookmarksCount={plan.attendeeCount}
                      />
                    </Box>
                  )}
                  {isSharedEvent(plan) && (
                    <MessageEventBtn
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        navigate(`/event/${plan.id}/chat`, {
                          state: {mentionUser: {id: profile.id, handle: profile.handle}},
                        });
                      }}>
                      <i className="fi fi-rr-paper-plane" aria-hidden="true" style={{fontSize: '12px'}} />
                      Message in Event Chat
                    </MessageEventBtn>
                  )}
                </Box>
              </Box>
            ))}

            {/* Sentinel for infinite scroll */}
            <div ref={sentinelRef} style={{height: 1}} />

            {activeQuery.isFetchingNextPage && (
              <Center py={4}>
                <Spinner size="md" color="#E02F84" />
              </Center>
            )}
          </>
        ) : (
          <Box textAlign="center" py={8}>
            <TextG14 mt="0" color="#757D84">
              {sharedOnly ? 'No shared plans yet' : 'No upcoming plans'}
            </TextG14>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default TheirPlans;
